import { useState } from 'react';
import { Upload, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input, Select } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/table';
import { crm } from '@/lib/crm-client';
// Wertimport bewusst über den schlanken Einstiegspunkt: `@epasu/core` zöge die
// Datenbank samt better-sqlite3 in den Browser-Bundle.
import { guessMapping } from '@epasu/core/mapping';
import type { ContactLifecycle } from '@epasu/core/constants';
import type {
  ParsedCsv,
  ColumnMapping,
  ImportPlan,
  ImportRowResult,
  Audience,
  LeadListWithCount,
} from '@epasu/core';
import { useEffect } from 'react';

type Step = 'upload' | 'map' | 'preview' | 'done';
type Status = ImportRowResult['status'];

/**
 * Zielfelder, nach Herkunft gruppiert. Eine Leadliste beschreibt meist Firmen
 * und hat als einzige Person ein `info@` — ohne die Firmenspalten bliebe von so
 * einer Datei fast nichts übrig.
 */
const GRUPPEN: { titel: string; felder: { key: keyof ColumnMapping; label: string }[] }[] = [
  {
    titel: 'Kontakt',
    felder: [
      { key: 'firstName', label: 'Vorname' },
      { key: 'lastName', label: 'Nachname' },
      { key: 'email', label: 'E-Mail *' },
      { key: 'phone', label: 'Telefon' },
      { key: 'role', label: 'Rolle' },
      { key: 'formOfAddress', label: 'Anredeform (Herr/Frau)' },
      { key: 'personalSalutation', label: 'Persönliche Anrede ({{anrede_persoenlich}})' },
      { key: 'salutation', label: 'Anrede ({{csv_anrede}})' },
    ],
  },
  {
    titel: 'Firma',
    felder: [
      { key: 'organizationName', label: 'Name' },
      { key: 'organizationIndustry', label: 'Branche' },
      { key: 'organizationRegion', label: 'Einsatzgebiet' },
      { key: 'organizationWebsite', label: 'Website' },
      { key: 'organizationEmail', label: 'E-Mail' },
      { key: 'organizationPhone', label: 'Telefon' },
      { key: 'organizationStreet', label: 'Straße' },
      { key: 'organizationPostalCode', label: 'PLZ' },
      { key: 'organizationCity', label: 'Ort' },
      { key: 'organizationNotes', label: 'Notiz' },
    ],
  },
];

const STATUS_LABEL: Record<Status, string> = {
  new: 'neu',
  duplicate: 'Dubletten',
  suppressed: 'gesperrt',
  invalid: 'ungültig',
};

const STATUS_VARIANT: Record<Status, 'success' | 'warning' | 'info' | 'danger'> = {
  new: 'success',
  duplicate: 'warning',
  suppressed: 'info',
  invalid: 'danger',
};

const ZEILEN_MAX = 50;

/**
 * Wert des Listen-Auswahlfelds, der keine bestehende Liste meint, sondern
 * „lege eine neue an“. Ein eigener Sentinel statt eines leeren Strings, weil
 * „keine Liste“ ebenfalls eine gueltige Wahl ist und beides sonst zusammenfiele.
 */
const NEUE_LISTE = '__neu__';

/**
 * Der Import spricht durchweg von mehreren Zeilen; `CONTACT_LIFECYCLE_LABELS`
 * liefert die Einzahl. Die Mehrzahl daraus abzuleiten waere fuer zwei Woerter
 * mehr Mechanik als sie wert ist.
 */
const LEBENSZYKLUS_MEHRZAHL: Record<ContactLifecycle, string> = {
  kontakt: 'Kontakte',
  lead: 'Leads',
};

export function ImportPage() {
  const [step, setStep] = useState<Step>('upload');
  const [audiences, setAudiences] = useState<Audience[]>([]);
  const [audienceId, setAudienceId] = useState('');
  const [parsed, setParsed] = useState<ParsedCsv | null>(null);
  const [mapping, setMapping] = useState<ColumnMapping>({});
  const [plan, setPlan] = useState<ImportPlan | null>(null);
  const [committed, setCommitted] = useState(0);
  /** Bestehende Kontakte, die beim letzten Import ihre Anrede bekamen. */
  const [nachgetragen, setNachgetragen] = useState(0);
  /** Vorhandene Leads, die der letzte Import in die Liste aufgenommen hat. */
  const [aufgenommen, setAufgenommen] = useState(0);
  const [statusFilter, setStatusFilter] = useState<Status | null>(null);
  const [lifecycle, setLifecycle] = useState<ContactLifecycle>('kontakt');
  const [listen, setListen] = useState<LeadListWithCount[]>([]);
  const [listId, setListId] = useState('');
  const [neueListe, setNeueListe] = useState('');
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  /** Name der Liste, in die der abgeschlossene Import geschrieben hat. */
  const [zielListe, setZielListe] = useState<string | null>(null);

  useEffect(() => {
    crm.audiences.list().then((a) => {
      setAudiences(a);
      const schools = a.find((x) => x.key === 'schools') ?? a[0];
      if (schools) setAudienceId(schools.id);
    });
    crm.leadLists.list().then(setListen);
  }, []);

  // Eine Liste gehoert immer genau einer Zielgruppe. Wer die Zielgruppe
  // wechselt, darf keine Liste aus der alten mitschleppen: der Import schreibt
  // die Mitgliedschaft direkt, ohne die Pruefung aus `leadLists.addMembers`.
  const listenDerZielgruppe = listen.filter(
    (l) => l.audienceId === audienceId && !l.archived,
  );
  /** Der Import schreibt in eine Liste — dann kommen auch vorhandene Leads hinein. */
  const inListe = lifecycle === 'lead' && listId !== '';
  useEffect(() => {
    setListId((aktuell) =>
      aktuell === NEUE_LISTE || listenDerZielgruppe.some((l) => l.id === aktuell)
        ? aktuell
        : '',
    );
    // Absichtlich nur an der Zielgruppe haengend — `listenDerZielgruppe` ist
    // bei jedem Rendern ein neues Array und wuerde die Auswahl sonst dauernd
    // zuruecksetzen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audienceId]);

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const p = await crm.imports.parse(text);
    setParsed(p);
    setMapping(guessMapping(p.headers));
    setStep('map');
  }

  async function runDryRun() {
    if (!parsed) return;
    const p = await crm.imports.plan({ audienceId, parsed, mapping });
    setPlan(p);
    setStatusFilter(null);
    setStep('preview');
  }

  /**
   * Schreibt den Import.
   *
   * Eine neu benannte Liste entsteht erst hier — nicht schon bei der Eingabe:
   * wer den Namen tippt und dann doch abbricht, soll keine leere Liste
   * hinterlassen.
   */
  async function commit() {
    if (!parsed || !plan) return;
    setFehler(null);
    setLaeuft(true);
    try {
      let ziel: string | null = null;
      if (lifecycle === 'lead') {
        if (listId === NEUE_LISTE) {
          const name = neueListe.trim();
          if (!name) {
            setFehler('Die neue Lead-Liste braucht einen Namen.');
            return;
          }
          const liste = await crm.leadLists.create({ name, audienceId });
          setListen(await crm.leadLists.list());
          ziel = liste.id;
          setZielListe(liste.name);
          // Die Liste ist jetzt eine bestehende. Bliebe die Auswahl auf dem
          // Platzhalter stehen, verlangte der naechste Durchlauf erneut einen
          // Namen — und legte eine zweite Liste gleichen Zwecks an.
          setListId(liste.id);
          setNeueListe('');
        } else if (listId) {
          ziel = listId;
          setZielListe(listenDerZielgruppe.find((l) => l.id === listId)?.name ?? null);
        } else {
          setZielListe(null);
        }
      } else {
        setZielListe(null);
      }

      const res = await crm.imports.commit({
        audienceId,
        parsed,
        mapping,
        plan,
        lifecycle,
        listId: ziel,
      });
      setCommitted(res.created);
      setNachgetragen(res.filled);
      setAufgenommen(res.addedToList);
      setStep('done');
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Der Import ist fehlgeschlagen.');
    } finally {
      setLaeuft(false);
    }
  }

  function neuStarten() {
    setParsed(null);
    setMapping({});
    setPlan(null);
    setStatusFilter(null);
    setFehler(null);
    // Zielgruppe, Lebenszyklus und Liste bleiben stehen: wer eine Datei
    // nachreicht, meint fast immer dasselbe Ziel. Nur der Platzhalter „neue
    // Liste“ waere ohne den Namen daneben eine Sackgasse.
    setListId((aktuell) => (aktuell === NEUE_LISTE ? '' : aktuell));
    setNeueListe('');
    setStep('upload');
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <h2 className="text-lg font-semibold">CSV-Import</h2>
      <StepBar step={step} />

      {step === 'upload' && (
        <Card>
          <CardContent className="p-8">
            <label className="flex cursor-pointer flex-col items-center gap-3 rounded-lg border-2 border-dashed border-border p-10 text-center hover:border-brand">
              <Upload className="h-8 w-8 text-brand" />
              <span className="text-sm font-medium">CSV-Datei auswählen</span>
              <span className="text-xs text-muted-foreground">
                Erste Zeile = Spaltenüberschriften
              </span>
              <input type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} />
            </label>
          </CardContent>
        </Card>
      )}

      {step === 'map' && parsed && (
        <Card>
          <CardContent className="space-y-4 p-6">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-sm font-medium">Zielgruppe</label>
                <Select value={audienceId} onChange={(e) => setAudienceId(e.target.value)}>
                  {audiences.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Anlegen als</label>
                <Select
                  value={lifecycle}
                  onChange={(e) => setLifecycle(e.target.value as ContactLifecycle)}
                >
                  <option value="kontakt">{LEBENSZYKLUS_MEHRZAHL.kontakt}</option>
                  <option value="lead">{LEBENSZYKLUS_MEHRZAHL.lead}</option>
                </Select>
                <p className="mt-1 text-xs text-muted-foreground">
                  {lifecycle === 'lead'
                    ? 'Kalt angeschriebene Adressen — sie stehen unter „Leads“.'
                    : 'Adressen, mit denen gearbeitet wird — sie stehen in der Akte ihrer Firma, auffindbar über die Suche.'}
                </p>
              </div>
            </div>

            {/*
              Die Liste gibt es nur zu Leads: `leadLists.addMembers` weist
              Nicht-Leads mit `kein_lead` ab, und der Import schreibt die
              Mitgliedschaft direkt an dieser Pruefung vorbei — die Auswahl darf
              also gar nicht erst anbietbar sein.
            */}
            {lifecycle === 'lead' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-sm font-medium">Lead-Liste</label>
                  <Select value={listId} onChange={(e) => setListId(e.target.value)}>
                    <option value="">— keine Liste —</option>
                    {listenDerZielgruppe.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name} ({l.memberCount})
                      </option>
                    ))}
                    <option value={NEUE_LISTE}>+ Neue Liste anlegen …</option>
                  </Select>
                </div>
                {listId === NEUE_LISTE && (
                  <div>
                    <label className="mb-1 block text-sm font-medium">Name der Liste</label>
                    <Input
                      value={neueListe}
                      onChange={(e) => setNeueListe(e.target.value)}
                      placeholder="z. B. Schulen Bayern, KW 12"
                    />
                  </div>
                )}
              </div>
            )}
            {GRUPPEN.map((g) => (
              <div key={g.titel} className="space-y-2">
                <h3 className="text-sm font-semibold">{g.titel}</h3>
                <div className="grid grid-cols-2 gap-3">
                  {g.felder.map((f) => (
                    <div key={f.key}>
                      <label className="mb-1 block text-xs font-medium text-muted-foreground">
                        {f.label}
                      </label>
                      <Select
                        value={mapping[f.key] ?? ''}
                        onChange={(e) =>
                          setMapping((m) => ({ ...m, [f.key]: e.target.value || undefined }))
                        }
                      >
                        <option value="">— nicht importieren —</option>
                        {parsed.headers.map((h) => (
                          <option key={h} value={h}>
                            {h}
                          </option>
                        ))}
                      </Select>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <div className="flex justify-end">
              <Button onClick={runDryRun} disabled={!mapping.email}>
                Vorschau (Dry-Run) <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 'preview' && plan && (
        <Card>
          <CardContent className="space-y-4 p-6">
            <div className="flex flex-wrap gap-2">
              {(Object.keys(STATUS_LABEL) as Status[]).map((s) => (
                <FilterBadge
                  key={s}
                  variant={STATUS_VARIANT[s]}
                  count={plan.summary[s]}
                  label={STATUS_LABEL[s]}
                  active={statusFilter === s}
                  onClick={() => setStatusFilter(statusFilter === s ? null : s)}
                />
              ))}
              <FilterBadge
                count={plan.summary.total}
                label="gesamt"
                active={statusFilter === null}
                onClick={() => setStatusFilter(null)}
              />
            </div>

            <PreviewTable
              results={plan.results}
              statusFilter={statusFilter}
              inListe={inListe}
            />
            {fehler && <p className="text-sm text-danger">{fehler}</p>}
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStep('map')}>
                Zurück
              </Button>
              {/*
                Auch ohne neue Zeilen gibt es etwas zu tun, wenn bekannte
                Kontakte ihre Anrede nachgetragen bekommen oder vorhandene Leads
                in die Liste kommen — sonst liesse sich eine Liste, deren Leads
                schon im CRM stehen, nie wieder fuellen.
              */}
              <Button
                variant="success"
                onClick={commit}
                disabled={
                  (plan.summary.new === 0 &&
                    plan.summary.fill === 0 &&
                    !(inListe && plan.summary.existingLeads > 0)) ||
                  laeuft
                }
              >
                {[
                  plan.summary.new > 0 &&
                    `${plan.summary.new} ${LEBENSZYKLUS_MEHRZAHL[lifecycle]} importieren`,
                  inListe &&
                    plan.summary.existingLeads > 0 &&
                    `${plan.summary.existingLeads} vorhandene ${plan.summary.existingLeads === 1 ? 'Lead' : 'Leads'} in die Liste`,
                  plan.summary.fill > 0 &&
                    `${plan.summary.fill} ${plan.summary.fill === 1 ? 'bestehenden' : 'bestehende'} ergänzen`,
                ]
                  .filter(Boolean)
                  .join(' · ') || `0 ${LEBENSZYKLUS_MEHRZAHL[lifecycle]} importieren`}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 'done' && (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
            <CheckCircle2 className="h-10 w-10 text-success" />
            <div className="text-lg font-semibold">
              {committed} {LEBENSZYKLUS_MEHRZAHL[lifecycle]} importiert
            </div>
            {zielListe && (
              <div className="text-sm text-muted-foreground">
                aufgenommen in die Lead-Liste „{zielListe}“
              </div>
            )}
            {aufgenommen > 0 && (
              <div className="text-sm text-muted-foreground">
                dazu {aufgenommen} {aufgenommen === 1 ? 'vorhandener Lead' : 'vorhandene Leads'}{' '}
                in die Liste aufgenommen
              </div>
            )}
            {nachgetragen > 0 && (
              <div className="text-sm text-muted-foreground">
                bei {nachgetragen} bestehenden{' '}
                {nachgetragen === 1 ? 'Kontakt' : 'Kontakten'} leere Anreden ergänzt
              </div>
            )}
            <Button variant="secondary" onClick={neuStarten}>
              Weiteren Import starten
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/**
 * Ein Badge, das die Tabelle auf seinen Status eingrenzt.
 *
 * Die Zahlen allein liessen die Frage „welche denn?“ offen — man sah, dass
 * zwölf Zeilen Dubletten sind, aber nicht welche.
 */
function FilterBadge({
  count,
  label,
  variant,
  active,
  onClick,
}: {
  count: number;
  label: string;
  variant?: 'success' | 'warning' | 'info' | 'danger';
  active: boolean;
  onClick: () => void;
}) {
  // Ein leerer Status hat nichts zu zeigen; anklickbar wäre er eine Sackgasse.
  const leer = count === 0;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={leer}
      aria-pressed={active}
      className={`rounded-full transition-opacity ${
        leer
          ? 'cursor-default opacity-50'
          : `cursor-pointer hover:opacity-80 ${
              active ? 'ring-2 ring-brand ring-offset-1 ring-offset-background' : ''
            }`
      }`}
    >
      <Badge variant={variant}>
        {count} {label}
      </Badge>
    </button>
  );
}

function PreviewTable({
  results,
  statusFilter,
  inListe,
}: {
  results: ImportRowResult[];
  statusFilter: Status | null;
  /** Der Import schreibt in eine Liste — vorhandene Leads kommen dann hinein. */
  inListe: boolean;
}) {
  const gefiltert = statusFilter ? results.filter((r) => r.status === statusFilter) : results;
  const sichtbar = gefiltert.slice(0, ZEILEN_MAX);

  return (
    <div className="space-y-2">
      <Table>
        <THead>
          <TR>
            <TH>Firma</TH>
            <TH>E-Mail</TH>
            <TH>Name</TH>
            <TH>Anrede</TH>
            <TH>Status</TH>
            <TH>Grund</TH>
          </TR>
        </THead>
        <TBody>
          {sichtbar.map((r) => (
            <TR key={r.rowIndex}>
              <TD>{r.organizationName ?? '—'}</TD>
              <TD>{r.email ?? '—'}</TD>
              <TD>{[r.firstName, r.lastName].filter(Boolean).join(' ') || '—'}</TD>
              <TD className={r.salutation ? undefined : 'text-muted-foreground'}>
                {r.salutation ?? '—'}
              </TD>
              <TD>
                <Badge variant={STATUS_VARIANT[r.status]}>{STATUS_LABEL[r.status]}</Badge>
              </TD>
              <TD className="text-muted-foreground">
                {[r.reason, inListe && r.existingLead && 'kommt in die Liste']
                  .filter(Boolean)
                  .join(' · ') || '—'}
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>

      {sichtbar.length < gefiltert.length && (
        <p className="text-xs text-muted-foreground">
          {sichtbar.length} von {gefiltert.length} gezeigt
        </p>
      )}
    </div>
  );
}

function StepBar({ step }: { step: Step }) {
  const steps: { key: Step; label: string }[] = [
    { key: 'upload', label: 'Datei' },
    { key: 'map', label: 'Mapping' },
    { key: 'preview', label: 'Vorschau' },
    { key: 'done', label: 'Fertig' },
  ];
  const idx = steps.findIndex((s) => s.key === step);
  return (
    <div className="flex items-center gap-2">
      {steps.map((s, i) => (
        <div key={s.key} className="flex items-center gap-2">
          <div
            className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
              i <= idx ? 'bg-brand text-white' : 'bg-secondary text-muted-foreground'
            }`}
          >
            {i + 1}
          </div>
          <span
            className={`text-sm ${i <= idx ? 'font-medium' : 'text-muted-foreground'}`}
          >
            {s.label}
          </span>
          {i < steps.length - 1 && <div className="h-px w-6 bg-border" />}
        </div>
      ))}
    </div>
  );
}
